import { useEffect, useRef, useState } from 'react';

/**
 * useLazyImage — delays loading an image src until the element
 * enters the viewport (IntersectionObserver). Reduces initial
 * network requests and speeds up page load.
 *
 * @param src      - The real image URL to load
 * @param rootMargin - How far before viewport to start loading (default 200px)
 */
const useLazyImage = (src: string, rootMargin = '200px'): [React.RefObject<HTMLImageElement | null>, string, boolean] => {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [activeSrc, setActiveSrc] = useState('');

  useEffect(() => {
    if (!src) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveSrc(src);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    const el = ref.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, [src, rootMargin]);

  return [ref, activeSrc, loaded];
};

export default useLazyImage;
